import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPinIcon, SearchIcon, UserIcon, WrenchIcon } from 'lucide-react';
import { useAdminData } from '../../../contexts/AdminDataContext';
import { useDismiss } from '../../../hooks/useDismiss';
interface Result {
  id: string;
  kind: 'Kunde' | 'Sag';
  title: string;
  detail: string;
  to: string;
}

/** Søger på tværs af kunder, sager og adresser. */
export function GlobalSearch({
  onNavigate
}: {
  onNavigate?: () => void;
}) {
  const {
    customers,
    cases
  } = useAdminData();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(wrapRef, open, close);
  const results = useMemo<Result[]>(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const matches = (...values: string[]) => values.some(value => value?.toLowerCase().includes(q));
    const customerHits: Result[] = customers.filter(c => matches(c.name, c.address, c.phone, c.email)).slice(0, 5).map(c => ({
      id: `k-${c.id}`,
      kind: 'Kunde',
      title: c.name,
      detail: c.address || c.phone || c.email,
      to: `/admin/kunder/${c.id}`
    }));
    const caseHits: Result[] = cases.filter(c => matches(c.title, c.address, c.customerName, c.number)).slice(0, 6).map(c => ({
      id: `s-${c.id}`,
      kind: 'Sag',
      title: `#${c.number} ${c.title}`,
      detail: [c.customerName, c.address].filter(Boolean).join(' · '),
      to: `/admin/sager/${c.id}`
    }));
    return [...customerHits, ...caseHits];
  }, [query, customers, cases]);
  function go(result: Result) {
    navigate(result.to);
    setQuery('');
    setOpen(false);
    onNavigate?.();
  }
  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!results.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(index => (index + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(index => (index - 1 + results.length) % results.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      go(results[active]);
    }
  }
  const showPanel = open && query.trim().length >= 2;
  return <div ref={wrapRef} className="relative w-full" data-id="element-410" data-mp="v1d8ha0zv6">
      <label htmlFor="admin-search" className="sr-only" data-id="element-411" data-mp="3hi9yq24rq2">
        Søg efter kunder, sager og adresser
      </label>
      <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" aria-hidden="true" data-id="element-412" data-mp="3rdpnnaczkfh8" />
      <input id="admin-search" type="search" autoComplete="off" value={query} onChange={event => {
      setQuery(event.target.value);
      setActive(0);
      setOpen(true);
    }} onFocus={() => setOpen(true)} onKeyDown={onKeyDown} placeholder="Søg efter kunder, sager, adresser …" role="combobox" aria-expanded={showPanel} aria-controls="admin-search-results" className="h-11 w-full rounded-xl border border-line bg-white pl-10 pr-4 text-[15px] text-ink placeholder:text-ink-faint transition-colors duration-150 ease-smooth focus:border-accent/60 focus:outline-none" data-id="element-413" data-mp="nrqy8k0lal9a" />

      {showPanel && <div id="admin-search-results" role="listbox" className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 overflow-hidden rounded-xl border border-line bg-white shadow-lift" data-id="element-414" data-mp="1e7hxep4dw9ry">
          {results.length === 0 ? <p className="px-4 py-5 text-sm text-ink-muted" data-id="element-415" data-mp="1s1inyvl6mqh8">Ingen resultater for “{query.trim()}”.</p> : <ul className="max-h-80 overflow-y-auto py-1.5" data-id="element-416" data-mp="1x0luylkq36ru">
              {results.map((result, index) => {
          const Icon = result.kind === 'Kunde' ? UserIcon : WrenchIcon;
          return <li key={result.id} role="option" aria-selected={index === active} data-id="element-417" data-mp="1fb9mgfanqfv4" data-mpi={index}>
                    <button type="button" onMouseEnter={() => setActive(index)} onClick={() => go(result)} className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors duration-100 ${index === active ? 'bg-canvas-deep' : ''}`} data-id="element-418" data-mp="1t1lk0tq0h6tn">
                      <Icon className="h-4 w-4 shrink-0 text-accent" strokeWidth={1.75} aria-hidden="true" data-id="element-419" data-mp="1adzy72z08a8v" />
                      <span className="min-w-0 flex-1" data-id="element-420" data-mp="15c0zi6e7m1i9">
                        <span className="block truncate text-[15px] text-ink" data-id="element-421" data-mp="2lqspkcnvuwoe">{result.title}</span>
                        {result.detail && <span className="flex items-center gap-1 truncate text-xs text-ink-muted" data-id="element-422" data-mp="2guliuhnnxh5e">
                            <MapPinIcon className="h-3 w-3 shrink-0" aria-hidden="true" data-id="element-423" data-mp="2mufjxj00ci5" />
                            <span className="truncate" data-id="element-424" data-mp="7l4ac70t9b38">{result.detail}</span>
                          </span>}
                      </span>
                      <span className="shrink-0 text-xs text-ink-faint" data-id="element-425" data-mp="3unqz5768us2z">{result.kind}</span>
                    </button>
                  </li>;
        })}
            </ul>}
        </div>}
    </div>;
}